import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ArrowLeft, Loader2, Search } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  getAccountingTemplate,
  getSuggestedMapping,
  listDogovors,
  saveNomenclatureMapping,
  searchNomenclature,
} from "@/api/mappings";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { extractErrorMessage } from "@/lib/api-errors";
import type {
  AccountingTemplateDTO,
  DogovorDTO,
  NomDTO,
  OneCRef,
} from "@/types/api";
import { useConnection } from "@/auth/ConnectionContext";

interface Props {
  open: boolean;
  sourceName: string;
  suggestions: OneCRef[];
  invoiceId: string;
  kontragentRefKey: string;
  organizationRefKey: string;
  onClose: () => void;
}

type Step = "nomenclature" | "dogovor";

export function NomenclaturePicker({
  open,
  sourceName,
  suggestions,
  invoiceId,
  kontragentRefKey,
  organizationRefKey,
  onClose,
}: Props): React.ReactElement {
  const queryClient = useQueryClient();
  const { activeConnectionId: connId } = useConnection();
  const [step, setStep] = useState<Step>("nomenclature");
  const [query, setQuery] = useState(sourceName);
  const [debouncedQuery, setDebouncedQuery] = useState(sourceName);
  const [selected, setSelected] = useState<OneCRef | null>(null);
  const [mappingKey, setMappingKey] = useState("");
  const [selectedDogovor, setSelectedDogovor] = useState<DogovorDTO | null>(
    null,
  );

  // Reset state every time dialog opens.
  useEffect(() => {
    if (open) {
      setStep("nomenclature");
      setQuery(sourceName);
      setDebouncedQuery(sourceName);
      setSelected(null);
      setMappingKey("");
      setSelectedDogovor(null);
    }
  }, [open, sourceName]);

  // Pre-fill the mapping key with the SOURCE text (from XML), not the 1С
  // item name. Ensures prefix-match works when XML and 1С names differ
  // (e.g., "WB" Latin in XML vs "ВБ" Cyrillic in 1С).
  useEffect(() => {
    if (selected) setMappingKey(sourceName);
  }, [selected, sourceName]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  const searchQuery = useQuery({
    queryKey: ["nomenclature-search", debouncedQuery],
    queryFn: () => searchNomenclature(debouncedQuery, 20),
    enabled: open && step === "nomenclature" && debouncedQuery.trim().length >= 2,
  });

  // --- Auto-skip step 1: if search returns exactly 1 exact match ---
  const [autoSkipped, setAutoSkipped] = useState(false);
  useEffect(() => {
    if (
      step === "nomenclature" &&
      !autoSkipped &&
      !selected &&
      searchQuery.data
    ) {
      // Merge suggestions + search results and check for single exact match.
      const all: OneCRef[] = [];
      const seen = new Set<string>();
      for (const s of suggestions) {
        if (!seen.has(s.ref_key)) { all.push(s); seen.add(s.ref_key); }
      }
      for (const nm of searchQuery.data) {
        if (!seen.has(nm.ref_key)) { all.push(_nomToRef(nm)); seen.add(nm.ref_key); }
      }
      const exact = all.filter(
        (s) => s.name.trim().toLowerCase() === sourceName.trim().toLowerCase(),
      );
      if (exact.length === 1) {
        setSelected(exact[0]!);
        setMappingKey(sourceName);
        setStep("dogovor");
        setAutoSkipped(true);
      }
    }
  }, [searchQuery.data, step, autoSkipped, selected, suggestions, sourceName]);

  const dogovorsQuery = useQuery({
    queryKey: ["dogovors", kontragentRefKey, organizationRefKey],
    queryFn: () => listDogovors(kontragentRefKey, organizationRefKey),
    enabled: open && step === "dogovor" && !!kontragentRefKey && !!organizationRefKey,
  });

  // --- Suggest mapping: auto-pick dogovor + accounting from history ---
  const suggestQuery = useQuery({
    queryKey: [
      "suggest-mapping",
      kontragentRefKey,
      organizationRefKey,
      selected?.ref_key,
    ],
    queryFn: () =>
      getSuggestedMapping(
        kontragentRefKey,
        organizationRefKey,
        selected!.ref_key,
      ),
    enabled:
      open &&
      step === "dogovor" &&
      selected !== null &&
      !!kontragentRefKey &&
      !!organizationRefKey,
    retry: false,
    staleTime: 60_000,
  });

  // Auto-select dogovor from suggestion (takes priority over single-dogovor auto-select).
  useEffect(() => {
    if (suggestQuery.data?.dogovor && selectedDogovor === null) {
      setSelectedDogovor(suggestQuery.data.dogovor);
    }
  }, [suggestQuery.data, selectedDogovor]);

  // Fallback: auto-select single contract when the list resolves AND no suggestion.
  useEffect(() => {
    if (
      dogovorsQuery.data &&
      dogovorsQuery.data.length === 1 &&
      selectedDogovor === null &&
      !suggestQuery.data
    ) {
      setSelectedDogovor(dogovorsQuery.data[0]!);
    }
  }, [dogovorsQuery.data, selectedDogovor, suggestQuery.data]);

  // Use accounting from suggestion when available, otherwise from explicit query.
  const accountingQuery = useQuery({
    queryKey: [
      "accounting-template",
      kontragentRefKey,
      organizationRefKey,
      selectedDogovor?.ref_key,
    ],
    queryFn: () =>
      getAccountingTemplate(
        kontragentRefKey,
        organizationRefKey,
        selectedDogovor!.ref_key,
      ),
    // Skip if we already have accounting from suggest-mapping.
    enabled:
      open &&
      step === "dogovor" &&
      selectedDogovor !== null &&
      !suggestQuery.data?.accounting,
    retry: false,
  });

  const effectiveAccounting =
    suggestQuery.data?.accounting ?? accountingQuery.data;

  const mutation = useMutation({
    mutationFn: saveNomenclatureMapping,
    onSuccess: () => {
      toast.success("Соответствие сохранено");
      queryClient.invalidateQueries({ queryKey: ["invoices", invoiceId, "preview", connId] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof AxiosError) {
        toast.error(err.response?.data?.detail ?? "Не удалось сохранить");
      } else {
        toast.error("Не удалось сохранить");
      }
    },
  });

  const handleNext = () => {
    if (!selected) return;
    setStep("dogovor");
  };

  const handleBack = () => {
    setStep("nomenclature");
  };

  const handleConfirm = () => {
    if (!selected || !selectedDogovor || !effectiveAccounting) return;
    const key = mappingKey.trim() || sourceName;
    mutation.mutate({
      source_name: key,
      one_c_nomenclature_key: selected.ref_key,
      one_c_nomenclature_name: selected.name,
      one_c_kontragent_key: kontragentRefKey,
      one_c_dogovor_key: selectedDogovor.ref_key,
      one_c_dogovor_name: selectedDogovor.description,
      one_c_accounting: effectiveAccounting,
    });
  };

  // --- render: nomenclature search step ---
  const merged: OneCRef[] = [];
  const seen = new Set<string>();
  for (const s of suggestions) {
    if (!seen.has(s.ref_key)) {
      merged.push(s);
      seen.add(s.ref_key);
    }
  }
  if (searchQuery.data) {
    for (const nm of searchQuery.data) {
      if (!seen.has(nm.ref_key)) {
        merged.push(_nomToRef(nm));
        seen.add(nm.ref_key);
      }
    }
  }

  const nomenclatureStepContent = (
    <>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Поиск в справочнике 1С…"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          autoFocus
        />
      </div>

      <div className="max-h-80 overflow-y-auto">
        {searchQuery.isLoading && (
          <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Поиск в 1С…
          </div>
        )}
        {searchQuery.isError && (
          <p className="p-3 text-sm text-destructive">
            Не удалось выполнить поиск:{" "}
            {extractErrorMessage(searchQuery.error, "нет ответа")}
          </p>
        )}
        {!searchQuery.isLoading && merged.length === 0 && (
          <p className="p-3 text-sm text-muted-foreground">
            Ничего не найдено. Попробуйте изменить запрос.
          </p>
        )}
        <ul className="space-y-2">
          {merged.map((s) => {
            const isSelected = selected?.ref_key === s.ref_key;
            return (
              <li key={s.ref_key}>
                <button
                  type="button"
                  onClick={() => setSelected(s)}
                  className={
                    "w-full rounded-md border p-3 text-left text-sm transition-colors " +
                    (isSelected
                      ? "border-primary bg-primary/5"
                      : "hover:bg-accent/50")
                  }
                >
                  <div className="font-medium">{s.name}</div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {selected && (
        <div className="space-y-2 rounded-md border bg-muted/30 p-3">
          <Label htmlFor="mapping-key" className="text-sm font-medium">
            Сохранить соответствие для текста:
          </Label>
          <Input
            id="mapping-key"
            value={mappingKey}
            onChange={(e) => setMappingKey(e.currentTarget.value)}
            placeholder={sourceName}
          />
          <p className="text-xs text-muted-foreground">
            Можно сократить, если в будущих документах будут похожие
            наименования с другими номерами или датами в конце (например,
            «Поставка №37057486»).
          </p>
        </div>
      )}
    </>
  );

  // --- render: dogovor + accounting step ---
  const dogovorStepContent = (
    <>
      <div className="space-y-3">
        <Label className="text-sm font-medium">Договор с контрагентом</Label>
        {suggestQuery.isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Ищем ранее использованный договор…
          </div>
        )}
        {!suggestQuery.isLoading && suggestQuery.data === null && dogovorsQuery.data && dogovorsQuery.data.length > 1 && (
          <p className="text-xs text-muted-foreground">
            Ранее использованный договор не найден — выберите из списка.
          </p>
        )}
        {dogovorsQuery.isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Загружаем договоры из 1С…
          </div>
        )}
        {dogovorsQuery.isError && (
          <p className="text-sm text-destructive">
            Не удалось загрузить договоры:{" "}
            {extractErrorMessage(dogovorsQuery.error, "нет ответа")}
          </p>
        )}
        {dogovorsQuery.data && dogovorsQuery.data.length === 0 && (
          <p className="text-sm text-destructive">
            В 1С нет активных договоров с этим контрагентом. Создайте
            договор в 1С и попробуйте снова.
          </p>
        )}
        {dogovorsQuery.data && dogovorsQuery.data.length > 0 && (
          <ul className="max-h-48 space-y-2 overflow-y-auto">
            {dogovorsQuery.data.map((d) => {
              const isSelected = selectedDogovor?.ref_key === d.ref_key;
              return (
                <li key={d.ref_key}>
                  <button
                    type="button"
                    onClick={() => setSelectedDogovor(d)}
                    className={
                      "w-full rounded-md border p-3 text-left text-sm transition-colors " +
                      (isSelected
                        ? "border-primary bg-primary/5"
                        : "hover:bg-accent/50")
                    }
                  >
                    <div className="font-medium">
                      {d.description}
                      {suggestQuery.data?.dogovor.ref_key === d.ref_key && (
                        <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-xs font-normal text-green-800">
                          ✓ ранее использовался
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {d.vid_dogovora}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {selectedDogovor && (
        <div className="space-y-2 rounded-md border bg-muted/30 p-3">
          <Label className="text-sm font-medium">
            Счета и аналитика (из последнего документа этого договора)
          </Label>
          {(accountingQuery.isLoading || suggestQuery.isLoading) && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Ищем шаблон в 1С…
            </div>
          )}
          {accountingQuery.isError && (
            <p className="text-sm text-destructive">
              {accountingQuery.error instanceof AxiosError
                ? (accountingQuery.error.response?.data?.detail ??
                  "Не удалось получить шаблон счетов")
                : "Не удалось получить шаблон счетов"}
            </p>
          )}
          {effectiveAccounting && (
            <AccountingPreview template={effectiveAccounting} />
          )}
        </div>
      )}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {step === "nomenclature"
              ? "Выберите номенклатуру в 1С"
              : "Выберите договор и подтвердите счета"}
          </DialogTitle>
          <DialogDescription>
            {step === "nomenclature" ? (
              <>
                Для позиции из документа: <b>{sourceName}</b>
              </>
            ) : (
              <>
                Номенклатура 1С: <b>{selected?.name}</b>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {step === "nomenclature" ? nomenclatureStepContent : dogovorStepContent}

        <DialogFooter>
          {step === "dogovor" && (
            <Button variant="ghost" onClick={handleBack}>
              <ArrowLeft className="size-4" />
              Назад
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          {step === "nomenclature" ? (
            <Button
              onClick={handleNext}
              disabled={!selected || mappingKey.trim().length === 0}
            >
              Далее
            </Button>
          ) : (
            <Button
              onClick={handleConfirm}
              disabled={
                !selectedDogovor ||
                !effectiveAccounting ||
                mutation.isPending ||
                (accountingQuery.isLoading || suggestQuery.isLoading)
              }
            >
              {mutation.isPending ? "Сохранение…" : "Сохранить"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccountingPreview({
  template,
}: {
  template: AccountingTemplateDTO;
}): React.ReactElement {
  const rows: [string, string][] = [
    ["Счёт затрат", template.schet_zatrat_name || template.schet_zatrat_key],
    ["Статья затрат", template.subkonto1_name || "—"],
    [
      "Счёт учёта НДС",
      template.schet_ucheta_nds_name || template.schet_ucheta_nds_key,
    ],
    ["Способ учёта НДС", template.sposob_ucheta_nds || "—"],
    ["Отражение в УСН", template.otrazhenie_usn || "—"],
  ];
  return (
    <div className="space-y-1 text-xs">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4">
          <span className="text-muted-foreground">{k}</span>
          <span className="font-mono text-foreground">{v}</span>
        </div>
      ))}
    </div>
  );
}

function _nomToRef(nm: NomDTO): OneCRef {
  return {
    ref_key: nm.ref_key,
    name: nm.description,
    inn: null,
    kpp: null,
    full_name: null,
  };
}
